import loadable from '@loadable/component';
import { type FunctionComponent, type PropsWithChildren, Component, createContext, useContext } from 'react';
import { FEATURE_FLAGS, OVERLAY_MARKER } from '../../../constants/general';
import {
	HOMEPAGE_LINK,
	LEARNING_CENTER_LINK,
	SHOP_ALL_CATEGORIES_LINK,
	SHOP_BY_LOOK_LINK,
	SHOWROOM_LINK,
	SUPPORT_EMAIL_US_LINK
} from '../../../constants/links';
import { SearchBarProvider, useSearchBarReducerContext } from '../../../contexts/search-bar/search-bar.context';
import { type AnalyticsEvent, AnalyticsHelper } from '../../../helpers/analytics/analytics.helper';
import { buildGTMHeaderCategoryLevelOneClick, buildGTMHeaderCategoryLevelTwoClick } from '../../../helpers/analytics/gtm/gtm-utils.helper';
import { useLiveChat } from '../../../hooks/analytics/live-chat/live-chat.hooks';
import { useContactInfo, useContactMethodStatus, useSiteInfo } from '../../../hooks/apollo/site/site.hooks';
import { useNotSmallBreakpointListener } from '../../../hooks/breakpoints/breakpoint-listeners.hook';
import { useFeature } from '../../../hooks/features/features.hooks';
import { useRouteChange } from '../../../hooks/routing/route-change.hook';
import { type Category as NavCategory } from '../../../types/site.types';
import { TextButton } from '../../buttons';
import { IconWithLabelButton } from '../../buttons/icon-with-label-button/icon-with-label-button.component';
import { MedalliaButton } from '../../buttons/medallia-button/medallia-button.component';
import { Drawer } from '../../common-components/drawer/drawer.component';
import { Expandable } from '../../common-components/expandable/expandable.component';
import { Link } from '../../common-components/link/link.component';
import { Tab, Tabs } from '../../common-components/tabs/tabs.component';
import { CloudinaryImage } from '../../images/cloudinary-image/cloudinary-image.component';
import { ProjectHeaderDropdown } from '../../project-components/project-header-dropdown/project-header-dropdown.component';
import MenuIcon from '../../svgs/menu.svg';
import OmniHomeSvg from '../../svgs/omni-home.svg';
import { CallCenterLink } from '../call-center-link/call-center-link.component';
import { CartLink } from '../cart-link/cart-link.component';
import { MobileAccountLink } from '../mobile-account-link/mobile-account-link.component';
import { SearchBar } from '../search-bar/search-bar.component';
import { AccountSection } from './account-section/account-section.component';
import { logoDimensions, navMenuLayout, divider } from './mobile-nav.css';
import { MobileSearchBar } from './mobile-search-bar/mobile-search-bar.component';

const LoadableTypeaheadDrawer = loadable(
	() => import(/* webpackChunkName: "typeahead-drawer" */ '../typeahead-drawer/typeahead-drawer.component'),
	{ resolveComponent: ({ TypeaheadDrawer }) => TypeaheadDrawer }
);

// #region Categories and Subs
const subCategoryImageOptions = {
	height: 140,
	width: 140,
	crop: 'fill' as const
};

export type SubCategoryProps = {
	name: string;
	publicID: string;
	url: string;
	analyticsEvent?: AnalyticsEvent;
};

/**
 * SubCategory - renders markup for a sub-category.
 */
const SubCategory: FunctionComponent<SubCategoryProps> = ({ name, publicID, url, analyticsEvent }) => {
	return (
		<Link analyticsEvent={analyticsEvent} url={url} className="w-50 link" color="grey-darker" underline={true} underlineHover={true}>
			<div className="center tc pb3">
				<CloudinaryImage description={name} publicID={publicID} options={subCategoryImageOptions} />
				<h2 className="f6 ma0 pt1">{name}</h2>
			</div>
		</Link>
	);
};

export type CategoryProps = {
	category: NavCategory;
};

/**
 * Category - renders markup for a category
 */
const Category: FunctionComponent<CategoryProps> = ({ category }) => {
	return (
		<div className="bb b--theme-grey-light w-100">
			<Expandable heading={category.name} onExpand={() => AnalyticsHelper.track(buildGTMHeaderCategoryLevelOneClick(category.name))}>
				<div className="flex flex-wrap mt3">
					<SubCategory
						analyticsEvent={{ data: buildGTMHeaderCategoryLevelTwoClick(`Shop all ${category.name}`) }}
						publicID={category.image.id}
						name={`Shop All ${category.name}`}
						url={category.url}
					/>
					{category.subCategories?.map((subCategory) => (
						<SubCategory
							analyticsEvent={{ data: buildGTMHeaderCategoryLevelTwoClick(subCategory.name) }}
							publicID={subCategory.image.id}
							name={subCategory.name}
							url={subCategory.url}
							key={subCategory.id}
						/>
					))}
				</div>
			</Expandable>
		</div>
	);
};
// #endregion

// #region Help Tab
export const HelpTab: FunctionComponent = () => {
	const { phone } = useContactInfo();
	const callCenterOpen = useContactMethodStatus('phone');
	const { setIsNavigationOpen } = useContext(MobileNavContext);
	const isLiveChatEnabled = useFeature(FEATURE_FLAGS.LIVE_CHAT);
	const initiateLiveChat = useLiveChat();

	const handleChatWithUs = () => {
		setIsNavigationOpen(false);
		initiateLiveChat();
	};

	return (
		<>
			<section className="tc">
				<h2 className="f5">Contact Us</h2>
				<nav aria-label="Contact Us">
					<ul className="list pa0">
						<li>
							<Link underline={false} color={'primary'} url={`tel:${phone.number}`}>
								{phone.number}
								{callCenterOpen && <div className="f7 theme-grey mt1 db">Available Now</div>}
							</Link>
						</li>
						{isLiveChatEnabled && (
							<li className="mt3">
								<>
									<TextButton onClick={handleChatWithUs}>Chat With Us</TextButton>
									<div className="f7 theme-grey mt1 db">Available Now</div>
								</>
							</li>
						)}
						<li className="mt3">
							<Link underline={false} color={'primary'} url={SUPPORT_EMAIL_US_LINK}>
								Email Us
							</Link>
						</li>
					</ul>
				</nav>
			</section>
			<section className="tc pt2">
				<h2 className="f5 ma0">Design Help &amp; Inspiration</h2>
				<nav aria-label="Design Help and Inspiration">
					<ul className="list pa0 ma0 pt2">
						<li className="ma0 pa2">
							<Link underline={false} color={'primary'} url={SHOWROOM_LINK}>
								Showrooms
							</Link>
						</li>
						<li className="ma0 pa2">
							<Link underline={false} color={'primary'} url={LEARNING_CENTER_LINK}>
								Learning Center
							</Link>
						</li>
						<li className="ma0 pa2">
							<Link underline={false} color={'primary'} url={SHOP_BY_LOOK_LINK}>
								Shop By Look
							</Link>
						</li>
						<li className="ma0 pa2">
							<MedalliaButton color="primary" />
						</li>
					</ul>
				</nav>
			</section>
		</>
	);
};
// #endregion
export type NavigationUIProps = {
	startTabName?: string;
	closeNavigation: () => void;
};
export const NavigationUI: FunctionComponent<NavigationUIProps> = ({ startTabName, closeNavigation }) => {
	const {
		data: { navigation }
	} = useSiteInfo();

	return (
		<Drawer ariaLabel="Site Navigation" onClose={closeNavigation} className="pa3 z-4">
			<Tabs startingTabName={startTabName || 'Shop'} tabControlClasses="w-90 fw7">
				<Tab name="Home" url={HOMEPAGE_LINK} onClick={closeNavigation} />
				<Tab name="Shop">
					<nav role="navigation" className="flex flex-column justify-between items-start mt3">
						<div className="pb2 bb b--theme-grey-light w-100">
							<Link
								url={SHOP_ALL_CATEGORIES_LINK}
								className="b"
								color="grey-darker"
								analyticsEvent={{ data: buildGTMHeaderCategoryLevelOneClick('Shop All Departments') }}>
								Shop All Departments
							</Link>
						</div>
						{navigation.map((category) => (
							<Category category={category} key={category.id} />
						))}
					</nav>
				</Tab>
				<Tab name="Account">
					<AccountSection closeNavigation={closeNavigation} />
				</Tab>
				<Tab name="Help">
					<HelpTab />
				</Tab>
			</Tabs>
		</Drawer>
	);
};

export type MobileNavProps = {
	startOpen?: boolean; // can be used to trigger the drawer immediately
	startTabName?: string; // which tab should be selected
};

export const MobileNav: FunctionComponent<MobileNavProps> = (props) => (
	<MobileNavProvider isNavigationOpen={props.startOpen || false}>
		<SearchBarProvider>
			<MobileNavComponent {...props} />
		</SearchBarProvider>
	</MobileNavProvider>
);

/**
 * MobileNav - renders markup for the small navigation
 */
export const MobileNavComponent: FunctionComponent<MobileNavProps> = ({ startTabName }) => {
	const { isNavigationOpen, setIsNavigationOpen, setIsTypeaheadOpen } = useContext(MobileNavContext);
	const closeNavigation = () => setIsNavigationOpen(false);
	const showNavigation = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
		event.preventDefault();
		event.currentTarget.blur();
		setIsNavigationOpen(true);
	};

	const typeaheadInDrawer = useFeature(FEATURE_FLAGS.TYPEAHEAD_MOBILE_VIEW);
	const [typeaheadState, typeaheadDispatch] = useSearchBarReducerContext();

	// Make sure menu is closed if the user navigates to another page or viewport is no longer small
	useRouteChange(closeNavigation);
	useNotSmallBreakpointListener((isNotSmall) => {
		if (isNotSmall) {
			closeNavigation();
			typeaheadDispatch({ type: 'typeaheadClosed' });
			setIsTypeaheadOpen(false);
		}
	});

	return (
		<>
			{isNavigationOpen && <NavigationUI startTabName={startTabName} closeNavigation={closeNavigation} />}
			<div className="flex flex-column items-center dn-ns">
				{/* Logo */}
				<Link url={HOMEPAGE_LINK} className="flex justify-center dn-ns w-100 tc pv2" ariaLabel="Navigate to home page">
					<OmniHomeSvg className={`w-100 ${logoDimensions}`} />
				</Link>
				{/* Search Bar */}
				<div className="w-100 ph3">
					{typeaheadInDrawer ? (
						<>
							<MobileSearchBar placeholder="Search" />
							{typeaheadState.showResults && <LoadableTypeaheadDrawer />}
						</>
					) : (
						<SearchBar onTypeaheadOpenChanged={setIsTypeaheadOpen} placeholder="Search" />
					)}
				</div>
				{/* Nav Pane */}
				<div className={`${navMenuLayout} items-center w-100 pt1`}>
					{/* Menu */}
					<IconWithLabelButton
						onClick={showNavigation}
						label="Menu"
						icon={<MenuIcon data-testid="MenuIcon" />}
						buttonClass="pv2 w-100"
						ariaLabel="Site Navigation"
						theme="primary"
					/>
					{/* Account */}
					<MobileAccountLink theme="primary" />
					{/* Projects */}
					<ProjectHeaderDropdown />
					{/* Call */}
					<CallCenterLink theme="primary" containerClass="flex-row-ns" />
					{/* Cart */}
					<CartLink isMobile={true} />
				</div>
				{/* Divider */}
				<div className={`${OVERLAY_MARKER} db dn-ns`} />
				<div className={`w-100 mb1 ${divider}`} />
			</div>
		</>
	);
};

// #region Mobile Nav Context
export type MobileNavContextState = Readonly<{
	isNavigationOpen: boolean;
	isTypeaheadOpen: boolean;
	setIsNavigationOpen: (isNavigationOpen: boolean) => void;
	setIsTypeaheadOpen: (isTypeaheadOpen: boolean) => void;
}>;

const mobileNavContextInitialState: MobileNavContextState = {
	isNavigationOpen: false,
	isTypeaheadOpen: false,
	setIsNavigationOpen: () => {},
	setIsTypeaheadOpen: () => {}
};

export const MobileNavContext = createContext<MobileNavContextState>(mobileNavContextInitialState);

export class MobileNavProvider extends Component<PropsWithChildren<{ isNavigationOpen: boolean }>, MobileNavContextState> {
	readonly state: MobileNavContextState = {
		isNavigationOpen: Boolean(this.props.isNavigationOpen),
		isTypeaheadOpen: false,
		setIsNavigationOpen: (isNavigationOpen: boolean) => {
			this.setState(() => ({ isNavigationOpen }));
		},
		setIsTypeaheadOpen: (isTypeaheadOpen: boolean) => {
			this.setState(() => ({ isTypeaheadOpen }));
		}
	};

	render() {
		return <MobileNavContext.Provider value={this.state}>{this.props.children}</MobileNavContext.Provider>;
	}
}
// #endregion
